import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { dataService, apiClient } from '@rti/shared';
import PdfDownloadShell from '@/components/PdfDownloadShell';
import { trackUserEvent } from '@/utils/userTracking';
import { buildDriversPdf, driversPdfFilename } from '@/utils/driversPdfBuilder';
import { useAuth } from '@/contexts/AuthContext';

const DriversPdfDownload: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'generating' | 'error' | 'success'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const generateAndDownloadPdf = async () => {
      const dateParam = searchParams.get('date');

      if (!dateParam || dateParam.length !== 8 || !/^\d{8}$/.test(dateParam)) {
        navigate('/drivers', { replace: true });
        return;
      }

      try {
        const year = dateParam.substring(0, 4);
        const month = dateParam.substring(4, 6);
        const day = dateParam.substring(6, 8);
        const selectedDate = `${year}-${month}-${day}`;
        const fileName = driversPdfFilename(selectedDate);

        try {
          const checkData = await apiClient.checkCachedPdfExists(fileName);
          if (checkData.exists) {
            const pdfBlob = await apiClient.downloadCachedPdf(fileName);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.location.href = pdfUrl;
            setStatus('success');
            await trackUserEvent('drivers_download', dateParam, { source: 'email_link', cache: 'hit' });
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
            return;
          }
        } catch (error) {
          console.log('No cached PDF found, generating new one');
        }

        setStatus('generating');

        const driversResponse = await dataService.getDriverData();
        const allReports = driversResponse || [];

        if (!Array.isArray(allReports) || allReports.length === 0) {
          setStatus('error');
          setErrorMessage('No driver data available for the selected date.');
          return;
        }

        const filteredReports = allReports.filter((report: any) => {
          const reportDate = report.date || report.snapshot_date;
          return reportDate === selectedDate;
        });

        if (filteredReports.length === 0) {
          setStatus('error');
          setErrorMessage(`No driver reports found for ${selectedDate}`);
          return;
        }

        const pdfBlob = await buildDriversPdf({ reports: filteredReports, selectedDate, userEmail: user?.email || '' });

        const msPerDay = 86400000;
        const isRecent = (Date.now() - new Date(selectedDate).getTime()) <= 7 * msPerDay;
        if (isRecent) {
          try {
            await apiClient.saveDriverPdf(pdfBlob, fileName, selectedDate);
          } catch (error) {
            console.error('Failed to save PDF to cache:', error);
          }
        }

        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.location.href = pdfUrl;
        setStatus('success');
        await trackUserEvent('drivers_download', dateParam, { source: 'email_link', cache: 'miss' });
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);

      } catch (error: any) {
        console.error('Error generating PDF:', error);
        setStatus('error');
        setErrorMessage(error?.message || 'Failed to generate PDF. Please try again.');
      }
    };

    generateAndDownloadPdf();
  }, [searchParams]);

  return <PdfDownloadShell status={status} errorMessage={errorMessage} />;
};

export default DriversPdfDownload;
